import React, { useEffect, useState } from 'react';
import { Search, Info } from 'lucide-react';
import { WorldGate } from '../../components/world/WorldGate';
import { WorldPageHeader } from '../../components/world/WorldPageHeader';
import { ExplorerFilters } from '../../components/world/ExplorerFilters';
import { AccessNote, MapList, MonsterList, SpawnList } from '../../components/world/ExplorerLists';
import { useMapsExplorer } from '../../hooks/useMapsExplorer';
import type { ExplorerTab } from '../../hooks/useMapsExplorer';
import type { WorldData } from '../../types/world';
const PAGE = 30;
export function MapsExplorer() {
  useEffect(() => {
    document.title = 'Mapas, monstruos y spots · Guía MU Vanguard';
  }, []);
  return <div data-id="element-2717" data-mp="4l2jywt0teen">
      <WorldPageHeader crumbs={[{
      label: 'Guía',
      to: '/guia'
    }, {
      label: 'Mapas y spots'
    }]} eyebrow="Explorador" title="Mapas, monstruos y spots" data-id="element-2718" data-mp="4kkhvjjog5yt">
        <p className="mt-3 max-w-2xl text-base text-gray-300 leading-relaxed" data-id="element-2719" data-mp="2pc33dbuzubzi">
          Dónde aparece cada monstruo, en qué coordenadas y qué nivel pide cada mapa para entrar.
        </p>
        <p className="mt-3 flex max-w-2xl items-start gap-2 text-sm text-gray-500" data-id="element-2720" data-mp="1az8osbs5vltp">
          <Info className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" data-id="element-2721" data-mp="16xn2jztqlv6u" />
          Es la configuración publicada en la guía, no un seguimiento en vivo del servidor. Poder entrar a un mapa no significa poder cazar allí.
        </p>
      </WorldPageHeader>
      <WorldGate data-id="element-2722" data-mp="1tvhyynzxzs8f">{w => <Explorer w={w} data-id="element-2723" data-mp="1tuyry2btyo96" />}</WorldGate>
    </div>;
}
function Explorer({
  w
}: {
  w: WorldData;
}) {
  const e = useMapsExplorer(w);
  const [limit, setLimit] = useState(PAGE);
  const {
    maps,
    monsters,
    npcs,
    spawns,
    undocumentedAccess,
    pj
  } = e.results;
  useEffect(() => setLimit(PAGE), [e.tab, e.q, e.mapFilter, e.kind, e.lvMin, e.lvMax, e.charLv]);
  const tabs: {
    id: ExplorerTab;
    label: string;
    count: number;
  }[] = [{
    id: 'mapas',
    label: 'Mapas',
    count: maps.length
  }, {
    id: 'monstruos',
    label: 'Monstruos',
    count: monsters.length
  }, {
    id: 'npc',
    label: 'NPC',
    count: npcs.length
  }, {
    id: 'apariciones',
    label: 'Apariciones',
    count: spawns.length
  }];
  const total = e.tab === 'mapas' ? maps.length : e.tab === 'monstruos' ? monsters.length : e.tab === 'npc' ? npcs.length : spawns.length;
  return <div data-id="element-2724" data-mp="2pl8tiqcnm5c8">
      <div className="flex h-12 items-center gap-3 rounded-full border border-white/15 bg-[#0A1628] px-4 focus-within:border-[#D4AF37]/70" data-id="element-2725" data-mp="2pkgzvl2mahol">
        <Search className="h-4 w-4 shrink-0 text-[#D4AF37]" aria-hidden="true" data-id="element-2726" data-mp="2zcwh2g63b28f" />
        <input type="search" value={e.q} onChange={ev => e.set('q', ev.target.value)} placeholder="Nombre de mapa o monstruo…" aria-label="Buscar mapa o monstruo" className="min-w-0 flex-1 bg-transparent text-[15px] text-white placeholder:text-gray-500 focus:outline-none" data-id="element-2727" data-mp="33dyk7834nyfo" />
      </div>

      <div className="mt-4" data-id="element-2728" data-mp="1e0la128c3g6n">
        <ExplorerFilters w={w} {...e} data-id="element-2729" data-mp="1e1d2i46mwxsp" />
      </div>

      <div role="tablist" aria-label="Tipo de resultado" className="mt-6 flex gap-1 overflow-x-auto border-b border-white/10" data-id="element-2730" data-mp="1iywj3aqt1mnt">
        {tabs.map(t => <button key={t.id} role="tab" type="button" aria-selected={e.tab === t.id} onClick={() => e.set('vista', t.id === 'mapas' ? '' : t.id)} className={`-mb-px flex h-12 shrink-0 items-center gap-2 border-b-2 px-4 text-sm font-semibold transition-colors duration-150 ${e.tab === t.id ? 'border-[#D4AF37] text-white' : 'border-transparent text-gray-400 hover:text-white'}`} data-id="element-2731" data-mp="16nvprptneghl">
            {t.label}
            <span className="rounded-full bg-white/[0.06] px-2 py-0.5 text-xs tabular-nums text-gray-400" data-id="element-2732" data-mp="1pcix6ezarhyc">{t.count}</span>
          </button>)}
      </div>

      <div role="tabpanel" className="mt-5" data-id="element-2733" data-mp="1ffea8cxmnigh">
        {pj !== undefined && e.tab === 'mapas' && <AccessNote count={undocumentedAccess} data-id="element-2734" data-mp="916gqw0j33m8" />}
        {e.kind === 'evento' && e.tab === 'apariciones' && <p className="mb-4 text-sm text-gray-400" data-id="element-2735" data-mp="3zh6hil4qtz7">spawns.csv no contiene apariciones de eventos. Consulta la pestaña Monstruos o la ficha de cada mapa para ver su presencia durante eventos.</p>}
        {total === 0 ? <p className="rounded-2xl border border-white/10 p-6 text-gray-300" data-id="element-2736" data-mp="1xnre44786hnl">No hay resultados con estos filtros. Prueba otro nombre o quita algún filtro.</p> : <>
            {e.tab === 'mapas' && <MapList w={w} maps={maps.slice(0, limit)} pj={pj} data-id="element-2737" data-mp="2blpo6xeijrxa" />}
            {e.tab === 'monstruos' && <MonsterList w={w} monsters={monsters.slice(0, limit)} data-id="element-2738" data-mp="2bjv57omsy9vu" />}
            {e.tab === 'npc' && <>
                <p className="mb-4 max-w-2xl text-sm text-gray-400" data-id="element-2739" data-mp="21o1hd93mutyr">NPC de la guía, NPC que entregan misiones y entradas de nivel 0 del archivo de monstruos (NPC, guardias, puertas u objetos). No se cazan.</p>
                <MonsterList w={w} monsters={npcs.slice(0, limit)} npc data-id="element-2740" data-mp="q83e5lv4z25g" />
              </>}
            {e.tab === 'apariciones' && <SpawnList w={w} spawns={spawns.slice(0, limit)} data-id="element-2741" data-mp="v90udswl5m4b" />}
            {total > limit && <div className="mt-6 flex justify-center" data-id="element-2742" data-mp="39dnn8xx6yysg">
                <button type="button" onClick={() => setLimit(l => l + PAGE * 2)} className="h-11 rounded-full border border-white/15 px-6 text-sm font-semibold text-gray-200 transition-colors duration-150 hover:border-[#D4AF37]/50 hover:text-white" data-id="element-2743" data-mp="34gnh6n0cdw1c">
                  Mostrar más ({total - limit} restantes)
                </button>
              </div>}
          </>}
      </div>
    </div>;
}